import { BATTLE_ROYALE, type RoyalePhase, type RoyaleStatus } from "@tiny-seas/shared";

/** How the local player relates to the current battle royale round. */
export type RoyaleRole = "sailing" | "choosing" | "spectating" | "queued";

export interface RoyaleSnapshot {
  phase: RoyalePhase;
  phaseIn: number;
  round: number;
  alive: number;
  radius: number;
  winner: string;
  winnerSlot: number;
  winReason: string;
  crew: number;
  queued: boolean;
}

export type PhaseTone = "waiting" | "starting" | "live" | "over";
export interface PhaseChip { tone: PhaseTone; tag: string; detail: string; role: string }
export interface PhaseSplash { title: string; text: string; tone: "info" | "good" | "bad"; big: boolean; seconds: number }
export interface RoyaleResults { title: string; reason: string; next: string }
export interface SpectatorView { target: string; status: string; canWait: boolean }

const HOLD_MIN = Math.round(BATTLE_ROYALE.treasureHold / 60);

const ROLE_LABEL: Record<RoyaleRole, string> = {
  sailing: "⚓ Sailing",
  choosing: "Choosing a crew",
  spectating: "👁 Spectating",
  queued: "⏳ Sailing next round",
};

export function royaleRole(s: RoyaleSnapshot): RoyaleRole {
  if (s.crew >= 0) return "sailing";
  if (s.queued) return "queued";
  if (s.phase === "waiting" || s.phase === "countdown") return "choosing";
  return "spectating";
}

export function phaseChip(s: RoyaleSnapshot): PhaseChip {
  const n = Math.ceil(s.phaseIn);
  const role = ROLE_LABEL[royaleRole(s)];
  switch (s.phase) {
    case "waiting":
      return { tone: "waiting", tag: "WAITING", detail: "No round running · pick a crew to start one", role };
    case "countdown":
      return { tone: "starting", tag: "STARTING", detail: `Round ${s.round} starts in ${n}s`, role };
    case "active":
      return { tone: "live", tag: "LIVE", detail: `Round ${s.round} · ${s.alive} ships afloat · ring ${Math.round(s.radius)} m`, role };
    case "ended":
      return { tone: "over", tag: "ROUND OVER", detail: `Next round in ${n}s`, role };
  }
}

/** Centre splash for a phase transition between two snapshots, if any. */
export function royaleSplash(prev: RoyaleSnapshot | null, next: RoyaleSnapshot): PhaseSplash | null {
  if (next.phase === "ended") return null;
  const n = Math.ceil(next.phaseIn);
  const role = royaleRole(next);
  if (!prev || prev.phase !== next.phase) {
    if (next.phase === "countdown") {
      return {
        title: `Round ${next.round}`,
        text: role === "sailing" ? `Man your ship. The battle starts in ${n}s.` : "Pick a crew to sail in this round.",
        tone: "info", big: false, seconds: 3,
      };
    }
    if (next.phase === "active") {
      if (!prev) {
        if (role === "sailing") return null;
        return {
          title: "Round in progress",
          text: `Round ${next.round} is under way. Watch the battle, or join a crew that's still afloat.`,
          tone: "info", big: false, seconds: 4,
        };
      }
      return {
        title: "Battle begins!",
        text: role === "sailing"
          ? `Be the last ship afloat, or hold the treasure for ${HOLD_MIN} minutes.`
          : `Round ${next.round} is under way. You're spectating.`,
        tone: "good", big: false, seconds: 2.5,
      };
    }
    return null;
  }
  if (next.phase === "countdown") {
    if (n >= 1 && n <= 5 && n !== Math.ceil(prev.phaseIn)) {
      return { title: String(n), text: "Get ready!", tone: "info", big: true, seconds: 0.9 };
    }
    return null;
  }
  if (next.phase === "active" && prev.crew >= 0 && next.crew < 0) {
    return {
      title: "Your ship sank",
      text: `You're out of round ${next.round}. Spectating until round ${next.round + 1}.`,
      tone: "bad", big: false, seconds: 4,
    };
  }
  return null;
}

export function royaleResults(s: RoyaleSnapshot): RoyaleResults | null {
  if (s.phase !== "ended") return null;
  const n = Math.ceil(s.phaseIn);
  const title = s.winnerSlot >= 0 && s.winnerSlot === s.crew
    ? "🏆 Victory!"
    : s.winner ? `🏆 The ${s.winner} won` : "No ship survived";
  const reason = s.winReason === "last"
    ? "Last ship afloat."
    : s.winReason === "treasure" ? `Held the treasure for ${HOLD_MIN} minutes.` : "Every ship went down.";
  const next = royaleRole(s) === "spectating"
    ? `Round ${s.round + 1} starts in ${n}s. Pick a crew when it begins.`
    : `Round ${s.round + 1} starts in ${n}s. You'll sail in it.`;
  return { title, reason, next };
}

export function spectatorView(s: RoyaleSnapshot, targetName: string | null): SpectatorView | null {
  const role = royaleRole(s);
  if (role !== "spectating" && role !== "queued") return null;
  if (s.phase !== "active" && s.phase !== "ended") return null;
  return {
    target: targetName ? `Spectating ${targetName} · E: next ship` : "No ships afloat to watch",
    status: s.queued
      ? `You're queued for round ${s.round + 1}. Your crew sets sail when it starts.`
      : "Join a crew that's still afloat now, or start a new crew next round.",
    canWait: !s.queued,
  };
}

/** Lobby line describing the royale room's round before joining. */
export function lobbyRoyaleText(r: RoyaleStatus): string {
  switch (r.phase) {
    case "waiting":
      return "No round running yet. Set sail to start the countdown.";
    case "countdown":
      return `Round ${r.round} starts in about ${r.phaseIn}s. Set sail now to join it.`;
    case "active":
      return `Round ${r.round} in progress · ${r.alive} ships afloat. You'll spectate until it ends, or join a crew that's still afloat.`;
    case "ended":
      return `Round ${r.round} just ended. The next round starts in about ${r.phaseIn}s.`;
  }
}
